import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, InputAdornment, IconButton, useTheme,
  Tooltip, Paper, Grid, FormControl, Select, MenuItem, Chip, Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Hub as CorrelationIcon,
  Link as LinkIcon,
  DoNotDisturb as DismissIcon,
  OpenInNew as EscalateIcon,
} from '@mui/icons-material';
import { RootState, Correlation, CorrelationEntity } from '../types';
import { fetchCorrelations, fetchCorrelationById, updateCorrelationStatus } from '../store/slices/correlationsSlice';
import { fetchClients } from '../store/slices/clientsSlice';
import { DataTable, Column } from '../components/common';
import { getSeverityStyles, getSeverityChipSx } from '../theme/mappers/ticketMappers';
import { getRelativeTimeString } from '../utils/dateUtils';

const ROLE_COLORS: Record<string, string> = {
  indicator: '#ef4444',
  actor:     '#f97316',
  target:    '#3b82f6',
  victim:    '#3b82f6',
};

const EntityBadge: React.FC<{ entity: CorrelationEntity }> = ({ entity }) => {
  const theme = useTheme();
  const color = ROLE_COLORS[entity.role?.toLowerCase()] ?? theme.palette.text.secondary;
  return (
    <Tooltip title={`${entity.role ?? 'unknown'} · ${entity.type}`} arrow>
      <Chip
        label={entity.value.length > 22 ? entity.value.slice(0, 20) + '…' : entity.value}
        size="small"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          fontWeight: 600,
          height: 20,
          backgroundColor: `${color}18`,
          color,
          border: `1px solid ${color}44`,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Tooltip>
  );
};

const CorrelationsPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { correlations, isLoading, error } = useSelector((state: RootState) => state.correlations);
  const { clients } = useSelector((state: RootState) => state.clients);

  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
  }, []);

  const load = () => {
    dispatch(fetchCorrelations({
      status: statusFilter,
      client_id: clientFilter || undefined,
    }) as any);
  };

  useEffect(() => { load(); }, [statusFilter, clientFilter]);

  const handleRowClick = (row: Correlation) => {
    dispatch(fetchCorrelationById(row.id) as any);
  };

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dispatch(updateCorrelationStatus({ id, status: 'dismissed' }) as any);
  };

  const handleEscalate = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dispatch(updateCorrelationStatus({ id, status: 'escalated' }) as any);
  };

  const filtered = correlations.filter((c) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      c.title.toLowerCase().includes(q) ||
      c.entities.some((e) => e.value.toLowerCase().includes(q))
    );
  });

  const clientName = (clientId: string) =>
    clients.find((c) => c.id === clientId)?.name ?? clientId.slice(0, 8) + '…';

  const columns: Column<Correlation>[] = [
    {
      key: 'severity_highest',
      header: 'Severity',
      minWidth: 90,
      align: 'left',
      render: (row) => {
        if (!row.severity_highest) return (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>
        );
        const styles = getSeverityStyles(row.severity_highest as any, theme);
        return (
          <Chip
            label={row.severity_highest.toUpperCase()}
            size="small"
            sx={{ ...getSeverityChipSx(styles.color), backgroundColor: styles.bgColor }}
          />
        );
      },
    },
    {
      key: 'title',
      header: 'Title',
      minWidth: 200,
      align: 'left',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 300,
          }}
        >
          {row.title}
        </Typography>
      ),
    },
    {
      key: 'alert_count',
      header: 'Alerts',
      minWidth: 70,
      align: 'center',
      render: (row) => (
        <Chip
          label={row.alert_count}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 700,
            backgroundColor: `${theme.palette.primary.main}22`,
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}44`,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      ),
    },
    {
      key: 'correlation_score',
      header: 'Score',
      minWidth: 65,
      align: 'center',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.85rem',
            color: row.correlation_score >= 3
              ? theme.palette.error.main
              : theme.palette.warning.main,
          }}
        >
          {row.correlation_score}
        </Typography>
      ),
    },
    {
      key: 'entities',
      header: 'Shared Entities',
      minWidth: 220,
      align: 'left',
      render: (row) => {
        const visible = row.entities.slice(0, 3);
        const overflow = row.entities.length - visible.length;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {visible.map((e, i) => <EntityBadge key={i} entity={e} />)}
            {overflow > 0 && (
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                +{overflow}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      key: 'client_id',
      header: 'Client',
      minWidth: 120,
      align: 'left',
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {clientName(row.client_id)}
        </Typography>
      ),
    },
    {
      key: 'created_at',
      header: 'Detected',
      minWidth: 110,
      align: 'left',
      render: (row) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {row.detected_at[0] ? getRelativeTimeString(row.detected_at[0]) : '—'}
        </Typography>
      ),
    },
    {
      key: 'id',
      header: '',
      minWidth: 150,
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          {row.status === 'open' ? (
            <>
              <Tooltip title="Mark as escalated">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EscalateIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={(e) => handleEscalate(e, row.id)}
                  sx={{
                    height: 26, fontSize: '0.65rem', fontWeight: 700, px: 1, minWidth: 0,
                    color: theme.palette.warning.main,
                    borderColor: `${theme.palette.warning.main}55`,
                    '&:hover': { backgroundColor: `${theme.palette.warning.main}15`, borderColor: theme.palette.warning.main },
                  }}
                >
                  Escalate
                </Button>
              </Tooltip>
              <Tooltip title="Dismiss">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DismissIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={(e) => handleDismiss(e, row.id)}
                  sx={{
                    height: 26, fontSize: '0.65rem', fontWeight: 700, px: 1, minWidth: 0,
                    color: theme.palette.text.disabled,
                    borderColor: `${theme.palette.text.disabled}44`,
                    '&:hover': { backgroundColor: `${theme.palette.error.main}15`, color: theme.palette.error.main, borderColor: `${theme.palette.error.main}55` },
                  }}
                >
                  Dismiss
                </Button>
              </Tooltip>
            </>
          ) : (
            <Chip
              label={row.status}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                color: row.status === 'escalated' ? theme.palette.warning.main : theme.palette.text.disabled,
                backgroundColor: 'transparent',
                border: `1px solid ${row.status === 'escalated' ? theme.palette.warning.main + '55' : theme.palette.divider}`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      pt: 10, pb: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>

        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <CorrelationIcon sx={{ mt: 0.5, fontSize: 22, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}>
                Correlations
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Alerts grouped by shared indicators — possible coordinated attacks
              </Typography>
            </Box>
          </Box>

          {filtered.length > 0 && (
            <Chip
              icon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
              label={`${filtered.length} correlation${filtered.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                height: 22, fontSize: '0.65rem', fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                backgroundColor: 'transparent',
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.border?.main ?? theme.palette.divider}`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>

        {/* Filters */}
        <Paper sx={{ mb: 3, pr: 2, backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1, ml: 0.5, letterSpacing: '0.05em' }}>
                SEARCH
              </Typography>
              <TextField
                fullWidth
                placeholder="Search by title or entity value…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.tertiary, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.tertiary, height: 40, '& fieldset': { border: 'none' }, '&:hover': { backgroundColor: theme.palette.background.secondary }, '&.Mui-focused': { backgroundColor: theme.palette.background.default, boxShadow: `0 0 0 1px ${theme.palette.text.primary}` } } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1, ml: 0.5, letterSpacing: '0.05em' }}>
                CLIENT
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  displayEmpty
                  sx={{ backgroundColor: theme.palette.background.tertiary, height: 40, '& fieldset': { border: 'none' } }}
                >
                  <MenuItem value="">All clients</MenuItem>
                  {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1, ml: 0.5, letterSpacing: '0.05em' }}>
                STATUS
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ backgroundColor: theme.palette.background.tertiary, height: 40, '& fieldset': { border: 'none' } }}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="escalated">Escalated</MenuItem>
                  <MenuItem value="dismissed">Dismissed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.25 }}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={load}
                    sx={{ bgcolor: theme.palette.background.tertiary, color: theme.palette.info.main, borderRadius: 1, width: 40, height: 40, '&:hover': { bgcolor: theme.palette.info.main, color: theme.palette.background.tertiary, transform: 'rotate(180deg)' }, transition: 'all 0.4s ease' }}
                  >
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <DataTable<Correlation>
          columns={columns}
          data={filtered}
          rowKey={(row) => row.id}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          error={error}
          onRetry={load}
          emptyMessage={statusFilter === 'open' ? 'No open correlations' : `No ${statusFilter} correlations`}
          emptySubMessage={
            statusFilter === 'open'
              ? 'Correlations appear automatically when two or more alerts share an indicator'
              : undefined
          }
          loadingMessage="Loading correlations…"
          minTableWidth={700}
          rowHeight={56}
        />
      </Box>
    </Box>
  );
};

export default CorrelationsPage;
